import React, { useState, useEffect } from "react";
import API_BASE_URL from "../config";

const VehicleGPSIntegration = () => {
  const [vehicleLocation, setVehicleLocation] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  const fetchVehicleGPS = async () => {
    try {
      const response = await fetch(API_BASE_URL + "/vehicle/fetch-locations");
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setVehicleLocation(data[0]);
        setHistory((prev) =>
          [{ ...data[0], time: new Date().toLocaleTimeString() }, ...prev].slice(0, 10)
        );
      }
      setError("");
    } catch {
      setError("Unable to fetch GPS data. Check backend connection.");
    }
  };

  useEffect(() => {
    let interval;
    if (tracking) {
      fetchVehicleGPS();
      interval = setInterval(fetchVehicleGPS, 5000);
    }
    return () => clearInterval(interval);
  }, [tracking]);

  const s = {
    container: {
      fontFamily: "Arial, sans-serif",
      marginLeft: "278px",
      marginRight: "10px",
      backgroundColor: "#ffffff",
      color: "#000000",
      minHeight: "100vh",
    },
    pageHeader: {
      backgroundColor: "#FFC107",
      padding: "16px 24px",
      marginBottom: "0px",
    },
    pageHeaderText: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#000000",
      letterSpacing: "1px",
      textTransform: "uppercase",
      margin: 0,
    },
    contentBody: {
      padding: "24px",
    },
    sectionTitle: {
      fontSize: "15px",
      fontWeight: "700",
      color: "#000000",
      marginBottom: "4px",
    },
    sectionDivider: {
      border: "none",
      borderTop: "2px solid #FFC107",
      marginBottom: "16px",
    },
    gpsBox: {
      border: "1.5px solid #000000",
      backgroundColor: tracking ? "#fffde7" : "#f5f5f5",
      padding: "24px",
      textAlign: "center",
      marginBottom: "16px",
    },
    statusRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontSize: "13px",
      fontWeight: "700",
      color: "#000000",
      marginBottom: "16px",
    },
    dot: {
      width: "12px",
      height: "12px",
      borderRadius: "50%",
      backgroundColor: tracking ? "#FFC107" : "#cccccc",
      display: "inline-block",
      animation: tracking ? "pulse 1s infinite" : "none",
    },
    btnRow: {
      display: "flex",
      gap: "12px",
      justifyContent: "center",
    },
    startBtn: {
      backgroundColor: tracking ? "#cccccc" : "#FFC107",
      color: "#000000",
      border: "none",
      padding: "8px 28px",
      borderRadius: "0",
      cursor: tracking ? "not-allowed" : "pointer",
      fontWeight: "700",
      fontSize: "13px",
      textTransform: "uppercase",
    },
    stopBtn: {
      backgroundColor: !tracking ? "#cccccc" : "#000000",
      color: !tracking ? "#000000" : "#ffffff",
      border: "none",
      padding: "8px 28px",
      borderRadius: "0",
      cursor: !tracking ? "not-allowed" : "pointer",
      fontWeight: "700",
      fontSize: "13px",
      textTransform: "uppercase",
    },
    coordGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "12px",
      marginTop: "16px",
    },
    coordCard: {
      border: "1.5px solid #000000",
      padding: "12px 16px",
      textAlign: "center",
      backgroundColor: "#ffffff",
    },
    coordVal: {
      fontSize: "18px",
      fontWeight: "700",
      color: "#000000",
    },
    coordLabel: {
      fontSize: "11px",
      color: "#555555",
      marginTop: "4px",
      fontWeight: "700",
    },
    errorBox: {
      border: "1.5px solid #000000",
      backgroundColor: "#fffde7",
      padding: "8px 12px",
      fontSize: "12px",
      fontWeight: "700",
      color: "#000000",
      marginBottom: "12px",
      textAlign: "center",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "8px",
    },
    th: {
      backgroundColor: "#000000",
      color: "#ffffff",
      padding: "12px 16px",
      textAlign: "center",
      fontSize: "13px",
      fontWeight: "700",
      border: "1px solid #000000",
    },
    td: {
      border: "1px solid #000000",
      padding: "10px 14px",
      fontSize: "13px",
      textAlign: "center",
      backgroundColor: "#ffffff",
      color: "#000000",
    },
  };

  return (
    <div style={s.container}>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>

      {/* Yellow header bar */}
      <div style={s.pageHeader}>
        <h1 style={s.pageHeaderText}>Vehicle GPS Integration</h1>
      </div>

      <div style={s.contentBody}>

        {/* Tracking Control */}
        <div style={s.sectionTitle}>GPS Tracking Control</div>
        <hr style={s.sectionDivider} />

        <div style={s.gpsBox}>
          <div style={s.statusRow}>
            <span style={s.dot}></span>
            {tracking ? "Live tracking active — updating every 5 seconds" : "GPS tracking inactive"}
          </div>

          {error && <div style={s.errorBox}>⚠ {error}</div>}

          <div style={s.btnRow}>
            <button style={s.startBtn} disabled={tracking} onClick={() => setTracking(true)}>
              📡 Start Tracking
            </button>
            <button style={s.stopBtn} disabled={!tracking} onClick={() => setTracking(false)}>
              ■ Stop Tracking
            </button>
          </div>
        </div>

        {/* Current Location */}
        {vehicleLocation && (
          <>
            <div style={s.sectionTitle}>Current Location</div>
            <hr style={s.sectionDivider} />
            <div style={s.coordGrid}>
              <div style={s.coordCard}>
                <div style={s.coordVal}>
                  {vehicleLocation.lat?.toFixed(4) || vehicleLocation.latitude?.toFixed(4) || "—"}
                </div>
                <div style={s.coordLabel}>Latitude</div>
              </div>
              <div style={s.coordCard}>
                <div style={s.coordVal}>
                  {vehicleLocation.lng?.toFixed(4) || vehicleLocation.longitude?.toFixed(4) || "—"}
                </div>
                <div style={s.coordLabel}>Longitude</div>
              </div>
              <div style={s.coordCard}>
                <div style={s.coordVal}>{vehicleLocation.speed ?? "—"}</div>
                <div style={s.coordLabel}>Speed (km/h)</div>
              </div>
            </div>
          </>
        )}

        {/* History Table */}
        {history.length > 0 && (
          <>
            <div style={{ ...s.sectionTitle, marginTop: "24px" }}>Location History (last 10)</div>
            <hr style={s.sectionDivider} />
            <table style={s.table}>
              <thead>
                <tr>
                  {["Time", "Latitude", "Longitude", "Speed"].map((h) => (
                    <th style={s.th} key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i}>
                    <td style={s.td}>{h.time}</td>
                    <td style={s.td}>{(h.lat || h.latitude)?.toFixed(5)}</td>
                    <td style={s.td}>{(h.lng || h.longitude)?.toFixed(5)}</td>
                    <td style={s.td}>{h.speed} km/h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default VehicleGPSIntegration;